// AYA Multiservice — Écran 1 : configuration de la recharge (100% mock).
import React from "react";
import { Zap, ShieldCheck, ReceiptText, BookUser, X, ArrowRight, Users, Star, Clock, Gift, ChevronRight, Plus } from "lucide-react";
import { BrandLogo, InitialsAvatar } from "./BrandLogo";
import { fmtFcfa } from "./multiserviceApi";

const TABS = [
  { id: "credit", label: "Crédit téléphonique" },
  { id: "internet", label: "Forfaits Internet" },
  { id: "pass", label: "Pass et options" },
];

export default function ConfigStep({ catalog, offers, offersLoading, draft, patch, phoneError, canContinue, serviceAmount, onContinue, openContacts }) {
  const contacts = catalog?.contacts || [];
  const selContact = contacts.find((c) => c.id === draft.contactId);

  return (
    <div data-testid="ms-config-step">
      <div className="ms-banner" data-testid="ms-banner">
        <h2>Recharge téléphonique et Internet</h2>
        <p>Crédit, forfaits et pass pour vous ou un proche</p>
        <div className="ms-benefits">
          <span className="ms-benefit"><Zap size={13} /> Rapide</span>
          <span className="ms-benefit"><ShieldCheck size={13} /> Sécurisé</span>
          <span className="ms-benefit"><ReceiptText size={13} /> Reçu disponible après traitement</span>
        </div>
      </div>

      <div className="ms-ops" data-testid="ms-operators">
        {(catalog?.operators || []).map((op) => (
          <button key={op.id} className={`ms-op ${draft.operatorId === op.id ? "sel" : ""}`} data-testid={`ms-operator-${op.id}`}
            onClick={() => patch({ operatorId: op.id, offerId: null })}>
            <BrandLogo brand={op.brand} size={46} />
            <span className="ms-op-name">{op.name}</span>
          </button>
        ))}
      </div>

      <div className="ms-tabs" data-testid="ms-recharge-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`ms-tab ${draft.rechargeType === t.id ? "sel" : ""}`} data-testid={`ms-tab-${t.id}`}
            onClick={() => patch({ rechargeType: t.id, offerId: null, amount: t.id === "credit" ? draft.amount : null })}>
            {t.label}
          </button>
        ))}
      </div>

      <h3 className="ms-section-title">Numéro à recharger</h3>
      <div className="ms-phone-row">
        <span className="ms-prefix">
          <span className="ms-flag" aria-hidden="true"><i style={{ background: "#F97316" }} /><i style={{ background: "#fff" }} /><i style={{ background: "#059669" }} /></span>
          +225
        </span>
        <input className="ms-phone-input" data-testid="ms-phone-input" type="tel" inputMode="numeric" placeholder="07 12 34 56 78"
          value={draft.phone} onChange={(e) => patch({ phone: e.target.value.replace(/[^\d\s]/g, "").slice(0, 14), contactId: null })} />
        <button className="ms-contact-btn" data-testid="ms-open-contacts-btn" aria-label="Choisir dans mes contacts" onClick={() => openContacts("all")}>
          <BookUser size={18} color="#059669" />
        </button>
      </div>
      {phoneError && <p className="ms-field-err" data-testid="ms-phone-error">{phoneError}</p>}
      {selContact && (
        <div className="ms-contact-chip" data-testid="ms-selected-contact">
          <InitialsAvatar name={selContact.name} color={selContact.color} size={36} />
          <div style={{ flex: 1 }}>
            <div className="ms-chip-name">{selContact.name}</div>
            <div className="ms-chip-phone">{selContact.phone.replace(/(\d{2})(?=\d)/g, "$1 ")}</div>
          </div>
          <button className="ms-edit" data-testid="ms-clear-contact" aria-label="Retirer le contact" onClick={() => patch({ contactId: null, phone: "" })}><X size={16} /></button>
        </div>
      )}

      {draft.rechargeType === "credit" ? (
        <>
          <h3 className="ms-section-title">Choisissez un montant</h3>
          <div className="ms-amounts" data-testid="ms-amounts">
            {(catalog?.credit_amounts || []).map((a) => (
              <button key={a} className={`ms-amount ${draft.amount === a ? "sel" : ""}`} data-testid={`ms-amount-${a}`} onClick={() => patch({ amount: a })}>
                <b>{a.toLocaleString("fr-FR")}</b><span>FCFA</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <h3 className="ms-section-title">Choisissez une offre</h3>
          <div data-testid="ms-offers">
            {offersLoading ? (
              <p className="ms-empty" data-testid="ms-offers-loading">Chargement des offres…</p>
            ) : offers.length === 0 ? (
              <p className="ms-empty" data-testid="ms-offers-empty">Aucune offre disponible pour cette sélection.</p>
            ) : offers.map((o) => (
              <button key={o.id} className={`ms-offer ${draft.offerId === o.id ? "sel" : ""}`} data-testid={`ms-offer-${o.id}`} onClick={() => patch({ offerId: o.id })}>
                <span className="ms-radio" />
                <span className="ms-offer-body">
                  <span className="ms-offer-name">{o.name}</span>
                  <div className="ms-offer-desc">{o.description}{o.validity ? ` · Validité ${o.validity}` : ""}</div>
                </span>
                <span className="ms-offer-price">{fmtFcfa(o.amount)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <button className="ms-cta" data-testid="ms-continue-btn" disabled={!canContinue} onClick={onContinue}>
        Continuer{serviceAmount ? ` — ${fmtFcfa(serviceAmount)}` : ""} <ArrowRight size={17} />
      </button>

      <div className="ms-shortcuts" data-testid="ms-shortcuts">
        <button className="ms-shortcut" data-testid="ms-shortcut-proche" onClick={() => openContacts("all")}><Users size={16} /> Recharger un proche</button>
        <button className="ms-shortcut" data-testid="ms-shortcut-favoris" onClick={() => openContacts("favorites")}><Star size={16} /> Mes numéros favoris</button>
        <button className="ms-shortcut" data-testid="ms-shortcut-recentes" onClick={() => openContacts("recent")}><Clock size={16} /> Recharges récentes</button>
      </div>

      <h3 className="ms-section-title">Mes contacts récents</h3>
      <div className="ms-contacts-row" data-testid="ms-recent-contacts">
        <button className="ms-contact-item" data-testid="ms-contact-new" onClick={() => patch({ contactId: null, phone: "" })}>
          <span className="ms-brand" style={{ width: 44, height: 44, background: "#EAF7F0", color: "#059669" }}><Plus size={18} /></span>
          <b>Nouveau</b><span>&nbsp;</span>
        </button>
        {contacts.map((c) => (
          <button key={c.id} className="ms-contact-item" data-testid={`ms-contact-${c.id}`} onClick={() => patch({ contactId: c.id, phone: c.phone })}>
            <InitialsAvatar name={c.name} color={c.color} size={44} />
            <b>{c.name}</b><span>{c.phone.replace(/(\d{2})(?=\d)/g, "$1 ")}</span>
          </button>
        ))}
      </div>

      <button className="ms-gift" data-testid="ms-gift-banner" onClick={() => openContacts("all")}>
        <span className="ms-brand" style={{ width: 40, height: 40, background: "#FDE8CF", color: "#F97316" }}><Gift size={18} /></span>
        <span style={{ flex: 1 }}>
          <b>Offrir du crédit ou un forfait</b>
          <span>Faites plaisir à un proche en quelques secondes</span>
        </span>
        <ChevronRight size={17} color="#F97316" />
      </button>
    </div>
  );
}
